import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { blog } from "../lib/api";
import { ChevronLeft, Calendar } from "lucide-react";

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    blog.get(slug)
      .then((p) => {
        if (!alive) return;
        setPost(p);
        // Load 3 related posts (same category, excluding this one)
        blog.list({ category: p.category, limit: 6 }).then((rel) => {
          if (!alive) return;
          setRelated(rel.filter((r) => r.slug !== p.slug).slice(0, 3));
        }).catch(() => {});
      })
      .catch(() => alive && setNotFound(true));
    return () => { alive = false; };
  }, [slug]);

  // SEO meta + JSON-LD on post load
  useEffect(() => {
    if (!post) return;
    document.title = post.seo_title || `${post.title} | Onyx Rooftop DC`;
    setMeta("description", post.seo_description || post.excerpt);
    setMeta("keywords", post.seo_keywords || (post.tags || []).join(", "));
    // Open Graph
    setMetaProp("og:type", "article");
    setMetaProp("og:title", post.seo_title || post.title);
    setMetaProp("og:description", post.seo_description || post.excerpt);
    if (post.cover_image_url) setMetaProp("og:image", post.cover_image_url);
    setMetaProp("og:url", `https://onyxrooftopdc.com/blog/${post.slug}`);
    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", post.seo_title || post.title);
    setMeta("twitter:description", post.seo_description || post.excerpt);
    if (post.cover_image_url) setMeta("twitter:image", post.cover_image_url);

    // JSON-LD Article schema
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.seo_description || post.excerpt,
      image: post.cover_image_url ? [post.cover_image_url] : undefined,
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      author: { "@type": "Organization", name: post.author || "Onyx Rooftop DC" },
      publisher: {
        "@type": "Organization",
        name: "Onyx Rooftop DC",
        logo: { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_onyx-premium-reserve/artifacts/sjym8bm5_b623322b-3f2f-464f-8186-10972c9259c3-1581cab0.png" },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://onyxrooftopdc.com/blog/${post.slug}` },
      keywords: post.seo_keywords,
    };
    setJsonLd("post-jsonld", ld);
    return () => { removeJsonLd("post-jsonld"); };
  }, [post]);

  if (notFound) {
    return (
      <main className="pt-32 pb-24 min-h-screen bg-white text-center">
        <p className="eyebrow mb-4">— 404</p>
        <h1 className="font-display text-4xl text-ink mb-4">Article not found.</h1>
        <Link to="/blog" className="btn-luxe btn-luxe-ghost mt-6 inline-block">Back to Blog</Link>
      </main>
    );
  }

  if (!post) {
    return <main className="pt-32 pb-24 min-h-screen bg-white text-center text-muted italic">Loading…</main>;
  }

  return (
    <main data-testid="blog-post-page" className="bg-white min-h-screen">
      {/* HERO */}
      {post.cover_image_url && (
        <div className="relative h-[58vh] min-h-[420px] w-full overflow-hidden bg-ink pt-24">
          <img src={post.cover_image_url} alt={post.title} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 35%" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/90" />
          <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-10 h-full flex flex-col justify-end pb-12">
            <button onClick={() => navigate("/blog")} data-testid="blog-back" className="flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/70 hover:text-white mb-5 self-start">
              <ChevronLeft size={14} /> Back to Journal
            </button>
            <span className="eyebrow mb-3" style={{ color: "var(--onyx-gold-soft)" }}>— {post.category}</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] mb-4">{post.title}</h1>
            <div className="flex items-center gap-4 text-white/65 text-[11px] tracking-luxe uppercase">
              <span><Calendar size={12} className="inline mr-2" />{new Date(post.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
              <span>·</span>
              <span>{post.reading_minutes} min read</span>
              <span>·</span>
              <span>{post.author}</span>
            </div>
          </div>
        </div>
      )}

      {/* BODY */}
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
        {!post.cover_image_url && (
          <div className="mb-10 pt-8">
            <button onClick={() => navigate("/blog")} className="flex items-center gap-2 text-[10px] tracking-luxe uppercase text-muted hover:text-ink mb-5">
              <ChevronLeft size={14} /> Back to Journal
            </button>
            <span className="eyebrow mb-3">— {post.category}</span>
            <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight mb-4">{post.title}</h1>
          </div>
        )}

        <div className="onyx-prose" dangerouslySetInnerHTML={{ __html: post.body_html }} />

        {post.tags?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-black/10 flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="px-3 py-1.5 text-[10px] tracking-luxe-tight uppercase border border-black/15 text-black/65 rounded-sm">
                {t}
              </span>
            ))}
          </div>
        )}

        <style>{`
          .onyx-prose p { font-size: 1.05rem; line-height: 1.75; color: #2A2A2A; margin: 1rem 0 1.25rem; }
          .onyx-prose h2 { font-family: 'TikTok Sans', sans-serif; font-weight: 300; font-size: 1.85rem; color: #0A0A0A; margin: 2.5rem 0 1rem; letter-spacing: -0.015em; }
          .onyx-prose h3 { font-family: 'TikTok Sans', sans-serif; font-weight: 500; font-size: 1.35rem; color: #0A0A0A; margin: 2rem 0 0.75rem; }
          .onyx-prose ul, .onyx-prose ol { padding-left: 1.4rem; margin: 1.25rem 0; }
          .onyx-prose li { color: #2A2A2A; margin: 0.4rem 0; line-height: 1.7; }
          .onyx-prose li::marker { color: var(--onyx-gold); }
          .onyx-prose a { color: var(--onyx-gold); text-decoration: underline; text-underline-offset: 4px; }
          .onyx-prose a:hover { color: #0A0A0A; }
          .onyx-prose strong { color: #0A0A0A; font-weight: 600; }
        `}</style>
      </article>

      {/* CTA */}
      <section className="bg-ink text-white py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <span className="eyebrow" style={{ color: "var(--onyx-gold-soft)" }}>— Reserve Tonight</span>
          <h3 className="font-display text-3xl md:text-4xl mt-3 mb-5">Experience it firsthand.</h3>
          <p className="text-white/65 max-w-xl mx-auto mb-8">The best way to understand why Onyx is DC's #1 rooftop is to spend a night here. Reserve your table or join the free guestlist below.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/reservations" className="btn-luxe btn-luxe-gold">Reserve Table</Link>
            <Link to="/events" className="btn-luxe" style={{ borderColor: "rgba(255,255,255,0.4)", color: "#fff" }}>See Upcoming Events</Link>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-cream py-20">
          <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16">
            <span className="eyebrow">— Keep Reading</span>
            <h3 className="font-display text-3xl md:text-4xl mt-3 mb-10 text-ink">Related from the Journal</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-white border border-black/10 hover:border-ink transition flex flex-col">
                  <div className="aspect-[16/10] overflow-hidden bg-black">
                    {p.cover_image_url && <img src={p.cover_image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  </div>
                  <div className="p-6">
                    <p className="text-[10px] tracking-luxe uppercase text-gold mb-2">{p.category}</p>
                    <h4 className="font-display text-xl text-ink leading-tight mb-2 line-clamp-2">{p.title}</h4>
                    <span className="text-[10px] tracking-luxe uppercase text-ink group-hover:text-gold transition">Read →</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

// Helpers — manage <meta> + JSON-LD without React Helmet
function setMeta(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setMetaProp(prop, content) {
  if (!content) return;
  let el = document.querySelector(`meta[property="${prop}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}
function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}
